import React, { useState } from 'react';
import { Heart, History, Play, Trash2, ShieldCheck, Music2 } from 'lucide-react';
import { Track, UserSettings, TelegramUserProfile } from '../types';
import { tg } from '../services/telegram';
import { userStore, STREAM_QUALITY_OPTIONS } from '../services/userStore';
import { TrackCard } from './TrackCard';
import { CassetteReelIcon } from './icons/HardwareIcons';

interface ProfileViewProps {
  currentTrack: Track | null;
  isPlaying: boolean;
  favorites: Track[];
  history: Track[];
  settings: UserSettings;
  onSelectTrack: (track: Track) => void;
  onPlayAllFavorites: () => void;
  onSendFlacToBot?: (track: Track) => void;
}

export const ProfileView: React.FC<ProfileViewProps> = ({
  currentTrack,
  isPlaying,
  favorites,
  history,
  settings,
  onSelectTrack,
  onPlayAllFavorites,
  onSendFlacToBot,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'favorites' | 'history'>('favorites');
  const user: TelegramUserProfile = tg.getUserProfile();

  const currentQualityOption =
    STREAM_QUALITY_OPTIONS.find((q) => q.id === settings.streamQuality) || STREAM_QUALITY_OPTIONS[0];

  const getInitials = (name: string) => {
    if (!name) return 'AU';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  return (
    <div className="profile-view-container stagger-item stagger-2">
      {/* 1. Telegram User Profile Card */}
      <section className="profile-card">
        <div className="profile-card-top">
          <div className="profile-avatar-wrap">
            {user.photoUrl ? (
              <img src={user.photoUrl} alt={user.firstName} className="profile-avatar-img" />
            ) : (
              <div className="profile-monogram">
                <span className="profile-monogram-text">{getInitials(user.firstName)}</span>
              </div>
            )}
            <div className="profile-avatar-status-dot" title="Онлайн" />
          </div>

          <div className="profile-meta">
            <div className="profile-name-row">
              <h2 className="profile-name">{user.firstName} {user.lastName || ''}</h2>
              <span className="profile-id-pill">ID #{user.id}</span>
            </div>
            <div className="profile-username">@{user.username || 'audiophile'}</div>
            <div className="profile-status-badge">
              <span className="profile-status-dot" />
              <ShieldCheck size={12} color="var(--amber)" />
              <span className="profile-status-text">HI-RES LOSSLESS АКТИВЕН</span>
            </div>
          </div>
        </div>

        {/* 2. Audio Stats Row (3 Metrics) */}
        <div className="profile-stats-grid">
          <div
            className={`profile-stat-box ${activeSubTab === 'favorites' ? 'active' : ''}`}
            onClick={() => {
              tg.haptic('light');
              setActiveSubTab('favorites');
            }}
          >
            <div className="stat-label">В ИЗБРАННОМ</div>
            <div className="stat-value">{favorites.length}</div>
            <div className="stat-sub">треков сохранено</div>
          </div>

          <div
            className={`profile-stat-box ${activeSubTab === 'history' ? 'active' : ''}`}
            onClick={() => {
              tg.haptic('light');
              setActiveSubTab('history');
            }}
          >
            <div className="stat-label">ПРОСЛУШАНО</div>
            <div className="stat-value">{history.length}</div>
            <div className="stat-sub">недавних сессий</div>
          </div>

          <div className="profile-stat-box">
            <div className="stat-label">ТРАКТ</div>
            <div className="stat-value highlight">{currentQualityOption.shortLabel}</div>
            <div className="stat-sub">{currentQualityOption.badge}</div>
          </div>
        </div>
      </section>

      {/* 3. Sub-Tabs Toggle: Избранные / История */}
      <div className="profile-subtabs-bar">
        <button
          type="button"
          className={`profile-subtab-btn ${activeSubTab === 'favorites' ? 'active' : ''}`}
          onClick={() => {
            tg.haptic('light');
            setActiveSubTab('favorites');
          }}
        >
          <Heart size={14} fill={activeSubTab === 'favorites' ? 'var(--amber)' : 'none'} />
          <span>ИЗБРАННЫЕ ТРЕКИ ({favorites.length})</span>
        </button>

        <button
          type="button"
          className={`profile-subtab-btn ${activeSubTab === 'history' ? 'active' : ''}`}
          onClick={() => {
            tg.haptic('light');
            setActiveSubTab('history');
          }}
        >
          <History size={14} />
          <span>ИСТОРИЯ ({history.length})</span>
        </button>
      </div>

      {/* 4. Tab Content */}
      {activeSubTab === 'favorites' && (
        <section className="profile-list-section">
          <div className="profile-section-action-bar">
            <span className="profile-section-title">
              КОЛЛЕКЦИЯ ИЗБРАННОГО
            </span>
            {favorites.length > 0 && (
              <button
                type="button"
                className="btn-action-primary"
                onClick={() => {
                  tg.haptic('medium');
                  onPlayAllFavorites();
                }}
              >
                <Play size={12} fill="currentColor" />
                <span>Включить всё</span>
              </button>
            )}
          </div>

          {favorites.length > 0 ? (
            <div className="cassette-spine-list">
              {favorites.map((t, idx) => (
                <TrackCard
                  key={`fav-${t.id}`}
                  track={t}
                  index={idx}
                  isActive={currentTrack ? t.id === currentTrack.id : false}
                  isPlaying={isPlaying}
                  isFavorite={true}
                  onSelect={() => onSelectTrack(t)}
                  onToggleFavorite={() => userStore.toggleFavorite(t)}
                  onSendFlacToBot={onSendFlacToBot}
                />
              ))}
            </div>
          ) : (
            <div className="profile-empty-card">
              <div className="empty-icon-wrap">
                <Heart size={28} color="var(--champagne-dim)" />
              </div>
              <div className="empty-title">В избранном пока нет треков</div>
              <div className="empty-desc">
                Нажимайте на сердечко у любого трека в деке или поиске, чтобы добавить его в свой студийный архив.
              </div>
            </div>
          )}
        </section>
      )}

      {activeSubTab === 'history' && (
        <section className="profile-list-section">
          <div className="profile-section-action-bar">
            <span className="profile-section-title">
              НЕДАВНО ПРОСЛУШАНО
            </span>
            {history.length > 0 && (
              <button
                type="button"
                className="btn-action-secondary"
                onClick={() => {
                  tg.haptic('medium');
                  userStore.clearHistory();
                }}
              >
                <Trash2 size={12} />
                <span>Очистить историю</span>
              </button>
            )}
          </div>

          {history.length > 0 ? (
            <div className="cassette-spine-list">
              {history.map((t, idx) => (
                <TrackCard
                  key={`hist-${t.id}-${idx}`}
                  track={t}
                  index={idx}
                  isActive={currentTrack ? t.id === currentTrack.id : false}
                  isPlaying={isPlaying}
                  onSelect={() => onSelectTrack(t)}
                  onToggleFavorite={() => userStore.toggleFavorite(t)}
                  onSendFlacToBot={onSendFlacToBot}
                />
              ))}
            </div>
          ) : (
            <div className="profile-empty-card">
              <div className="empty-icon-wrap">
                <Music2 size={28} color="var(--champagne-dim)" />
              </div>
              <div className="empty-title">История прослушиваний пуста</div>
              <div className="empty-desc">
                Включайте мастер-ленты в деке или поиске, и они автоматически сохранятся в хронологическом журнале сессий.
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};
